import reportdailytransactionsService from '../../../services/reportdailytransactionsService';
import filterService from '../../../services/filterService';

import {
    setReportDailyTransactionsLoading,
    setReportDailyTransactionsError,
    setReportDailyTransactionsList,
    setReportDailyTransactionsCurrent,
    setReportDailyTransactionsPagination,
    setReportDailyTransactionsSort,
    setReportDailyTransactionsFilters,
    setReportDailyTransactionsDefaultFilter,
    setReportDailyTransactionsSettings,
    clearReportDailyTransactionsCurrent,
    clearReportDailyTransactionsError,
    setReportDailyTransactionsFilterLoading,
    changeReportDailyTransactionsFilterCondition,
    addReportDailyTransactionsFilterCondition,
    updateReportDailyTransactionsFilterConditionRule,
    updateReportDailyTransactionsFilterConditionValue,
    removeReportDailyTransactionsFilterCondition,
    setReportDailyTransactionsSelected,
    setReportDailyTransactionsDownloading,
    resetReportDailyTransactionsFiltersAndPagination,

    setReportDailyTransactionsClassicFilters
} from './reportdailytransactionsSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        if (!getState().reportdailytransactions.isSettingsInitialized) {
            dispatch(setReportDailyTransactionsLoading(true));
            const response = await reportdailytransactionsService.getSettings();

            dispatch(setReportDailyTransactionsSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportDailyTransactionsClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true);
            if (activeFilter) {
                dispatch(setReportDailyTransactionsDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        dispatch(setReportDailyTransactionsError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportDailyTransactionsLoading(false));
    }
};

export const fetchReportdailytransactions = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        const state = getState().reportdailytransactions;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportDailyTransactionsLoading(true));
        const response = await reportdailytransactionsService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportDailyTransactionsList(response));
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportDailyTransactionsError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportDailyTransactionsLoading(false));
    }
};

export const fetchReportDailyTransaction = (id) => async (dispatch) => {
    try {
        dispatch(setReportDailyTransactionsLoading(true));
        const response = await reportdailytransactionsService.getOne(id);
        dispatch(setReportDailyTransactionsCurrent(response.data));
    } catch (error) {
        dispatch(setReportDailyTransactionsError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportDailyTransactionsLoading(false));
    }
};

export const createReportDailyTransaction = (data) => async (dispatch) => {
    try {
        const response = await reportdailytransactionsService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportDailyTransaction = (id, data) => async (dispatch) => {
    try {
        const response = await reportdailytransactionsService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportDailyTransaction = (id) => async (dispatch) => {
    try {
        dispatch(setReportDailyTransactionsLoading(true));
        const response = await reportdailytransactionsService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportDailyTransactionsError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportDailyTransactionsLoading(false));
    }
};

export const downloadReportDailyTransaction = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportdailytransactions;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportDailyTransactionsDownloading(true));
        const response = await reportdailytransactionsService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportDailyTransactionsDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportdailytransactions:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportDailyTransactionsError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportDailyTransactionsDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportDailyTransactionsPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportDailyTransactionsSort(sort));
};

export const searchReportdailytransactions = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportdailytransactions.filters;
    dispatch(setReportDailyTransactionsFilters({ ...currentFilters, search: query }));
};

export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportDailyTransactionsDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportDailyTransactionsFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportDailyTransactionsFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportDailyTransactionsFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportDailyTransactionsFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportDailyTransactionsFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportDailyTransactionsFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportdailytransactions.settings;
            dispatch(setReportDailyTransactionsSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportDailyTransactionsFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportDailyTransactionsFilterLoading(false));
        dispatch(setReportDailyTransactionsError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportDailyTransactionsSelected(selectedRows));
    };
};

export const clearCurrentReportDailyTransaction = () => {
    return (dispatch) => {
        dispatch(clearReportDailyTransactionsCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportDailyTransactionsFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportdailytransactions.classicFilters;

    const updatedClassicFilters = { ...currentClassicFilters };

    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportDailyTransactionsClassicFilters(updatedClassicFilters));
};